import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MeService } from '@services/me.service';
import { DeviceService } from '@services/device.service';
import { MainService } from '@services/main.service';
import { BtnComponent } from '@components/btn/btn.component';
import { FieldComponent } from '@components/__drop_inputs_matSelect/field/field.component';
import { InputCtrlComponent } from '@components/__drop_inputs_matSelect/inputCtrl/inputCtrl.component';
import { HeadTitleComponent } from '@components/head-title/head-title.component';
import { BtnWrapComponent } from '@components/btn-wrap/btn-wrap.component';
import { HelperClass } from '@classes/Helper-Classes';
import { OtherService } from '@services/other.service';
import { ProfileService } from '@app/profile/profile.service';
import { TitleAndNavigationForProfileComponent } from '@app/profile/additional_components/title-and-navigation-for-profile/title-and-navigation-for-profile.component';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { rout_meProfile, rout_meProfileOfficial, TypePageProfile } from '@app/profile/profile';
import { Router } from '@angular/router';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, UntypedFormBuilder, Validators } from '@angular/forms';
import { PopupService } from '@services/popup.service';
import { PopupCalendarSyncDetails } from './popup-calendar-sync-details/popup-calendar-sync-details.component';
import { MeProfileOfficialService } from '@app/profile/me-profile-official/me-profile-official.service';
import { NotificationService } from '@services/notification.service';
import { INewRequest } from '@services/base-api';


interface IMeProfileCalendarForm {
  calendarSyncUrl: FormControl<string>;
}

@UntilDestroy()
@Component({
  selector: 'app-me-profile-calendar',
  standalone: true,
  imports: [
    CommonModule,
    BtnComponent,
    FieldComponent,
    FormsModule,
    InputCtrlComponent,
    ReactiveFormsModule,
    HeadTitleComponent,
    BtnWrapComponent,
    TitleAndNavigationForProfileComponent
  ],
  templateUrl: './me-profile-calendar.component.html',
  styleUrls: ['./me-profile-calendar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MeProfileCalendarComponent extends HelperClass {
  typePageProfile!: TypePageProfile;
  form!: FormGroup<IMeProfileCalendarForm>;
  calendarSyncUrl: string | undefined;

  constructor(
    public meS: MeService,
    public deviceS: DeviceService,
    public mainS: MainService,
    public otherS: OtherService,
    private router: Router,
    private popupS: PopupService,
    private notificationS: NotificationService,
    private formBuilder: UntypedFormBuilder,
    public profileS: ProfileService,
    public cd: ChangeDetectorRef,
    public officialProfileS: MeProfileOfficialService
  ) {
    super(cd);
    this.checkCurrentRout();
    this.createForm();
    this.getCalendarUrlAndUpdateForm();
  }

  private getCalendarUrlAndUpdateForm() {
    this.officialProfileS.getCalendarSyncUrl().pipe(untilDestroyed(this)).subscribe({
      next: (response: any) => {
        this.calendarSyncUrl = response?.calendarUrl;
        this.cd.detectChanges();
      }
    });
  }

  private createForm(): void {
    this.form = this.formBuilder.group({
      calendarSyncUrl: [this.calendarSyncUrl || '', [Validators.required]]
    });
  }

  get ctrl(): IMeProfileCalendarForm {
    return this.form?.controls;
  }

  checkCurrentRout(): void {
    if (this.router.url?.includes(rout_meProfile)) this.typePageProfile = rout_meProfile;
    if (this.router.url?.includes(rout_meProfileOfficial)) this.typePageProfile = rout_meProfileOfficial;
  }

  copyUrl() {
    navigator.clipboard.writeText(this.calendarSyncUrl || '').then(() => {
      this.notificationS.showNotification_success({ message: 'The calendar sync URL has been copied' } as INewRequest, '');
    });
  }

  showSyncWithGoogle() {
    this.popupS
      .open$(PopupCalendarSyncDetails, {
        calendarSyncUrl: this.calendarSyncUrl,
        calendarSystemToShow: 'google'
      }, false)
      .pipe(untilDestroyed(this));
  }

  showSyncWithApple() {
    this.popupS
      .open$(PopupCalendarSyncDetails, {
        calendarSyncUrl: this.calendarSyncUrl,
        calendarSystemToShow: 'apple'
      }, false)
      .pipe(untilDestroyed(this));
  }

  showSyncWithOutlook() {
    this.popupS
      .open$(PopupCalendarSyncDetails, {
        calendarSyncUrl: this.calendarSyncUrl,
        calendarSystemToShow: 'microsoft'
      }, false)
      .pipe(untilDestroyed(this));
  }
}
